import React, { useEffect, useRef, useState } from 'react';
import { useMutation } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import Flag from 'react-world-flags';

import ProfileEditInput from 'app/components/CommonStyled/ProfileEditInput';
import { CloseIcon } from 'app/components/shared/icons';
import { Form, Formik, FormikProps } from 'formik';
import { ICollectionParams, postCountries } from 'services/profile-editor';
import {
  profileDataSelector,
  setCountries,
} from 'store/reducers/profile-editor/profile-editor-reducer';
import * as Yup from 'yup';

import { useGetCountries, usePostCountries } from '../../hooks/useCountries';
import ProfileEditorDrawer, { ProfileEditorDrawerRefProps } from '../Drawer';
import { ProfileEditorDrawerTitle } from '../styled';
import {
  ProfileEditorDrawerCountriesPill,
  ProfileEditorDrawerCountriesPillIconContainer,
  ProfileEditorDrawerCountriesPillsContainer,
  ProfileEditorDrawerCountriesPillText,
  ProfileEditorDrawerCountriesWrapper,
} from './styled';

interface ProfileEditorDrawerCountriesProps {
  onClose: () => void;
}

export default function ProfileEditorDrawerCountries({
  onClose,
}: ProfileEditorDrawerCountriesProps) {
  const dispatch = useDispatch();
  const { countries, allCountries } = useSelector(profileDataSelector);
  const formikRef = useRef<FormikProps<any>>(null);
  const [optionsPicked, setOptionsPicked] = useState<any[]>([]);
  const [options, setOptions] = useState<any[]>([]);
  const drawerRef = useRef<ProfileEditorDrawerRefProps>(null);

  useEffect(() => {
    if (allCountries && allCountries.length > 0) {
      setOptions(
        allCountries.map((country) => ({
          value: country.uuid,
          label: country.name,
          code: country.code,
        }))
      );
    }
  }, [allCountries]);

  useEffect(() => {
    if (countries && countries.length > 0) {
      setOptionsPicked(
        countries.map((country) => ({
          value: country.uuid,
          label: country.name,
          code: country.code,
        }))
      );
    }
  }, [countries]);

  const handlePostCountries = async (params: ICollectionParams) => {
    const response = await postCountries(params);
    if (!response.ok) {
      throw new Error(response.originalError?.message);
    }

    dispatch(
      setCountries(
        optionsPicked.map((opt) => ({ uuid: opt.value, name: opt.label, code: opt.code }))
      )
    );

    return response.data;
  };

  const { mutate } = useMutation(handlePostCountries, drawerRef.current?.handleMutation);

  const countriesValidationSchema = Yup.object().shape({
    collection: Yup.array(),
  });

  const handleOptionsChanged = (options: any[]) => {
    setOptionsPicked(options);
  };

  const handleSubmit = (values) => {
    mutate({ collection: values.collection });
  };

  const handleRemoveCountry = (option) => {
    const newOptions = optionsPicked.filter((opt) => opt.value !== option.value);
    formikRef.current.setFieldValue(
      'collection',
      newOptions.map((opt) => opt.value)
    );
    setOptionsPicked(newOptions);
  };

  return (
    <Formik
      initialValues={{
        collection: countries.map((country) => country.uuid),
      }}
      validationSchema={countriesValidationSchema}
      onSubmit={handleSubmit}
      innerRef={formikRef}
    >
      {(props) => (
        <Form>
          <ProfileEditorDrawer
            ref={drawerRef}
            header={<ProfileEditorDrawerCountriesHeader />}
            needsSaving={props.dirty}
            onClose={onClose}
          >
            <ProfileEditorDrawerCountriesWrapper>
              <ProfileEditInput
                {...props}
                name={'collection'}
                label={'Where have you lived for 3 months or longer?'}
                tooltip={
                  'These countries will help find common ground between you and other people. It’s a great icebreaker!'
                }
                type={'select'}
                placeholder={'Select a country...'}
                options={options}
                onOptionsChanged={handleOptionsChanged}
              />
              <ProfileEditorDrawerCountriesPillsContainer>
                {optionsPicked &&
                  optionsPicked.length > 0 &&
                  optionsPicked.map((option, index) => (
                    <ProfileEditorDrawerCountriesPill key={option.value}>
                      <Flag style={{ marginRight: '5px' }} code={option.code} height="15" />
                      <ProfileEditorDrawerCountriesPillText>
                        {option.label}
                      </ProfileEditorDrawerCountriesPillText>
                      <ProfileEditorDrawerCountriesPillIconContainer
                        onClick={() => handleRemoveCountry(option)}
                      >
                        <CloseIcon width={9} height={9} fill={'var(--color-primary)'} />
                      </ProfileEditorDrawerCountriesPillIconContainer>
                    </ProfileEditorDrawerCountriesPill>
                  ))}
              </ProfileEditorDrawerCountriesPillsContainer>
            </ProfileEditorDrawerCountriesWrapper>
          </ProfileEditorDrawer>
        </Form>
      )}
    </Formik>
  );
}

function ProfileEditorDrawerCountriesHeader() {
  return <ProfileEditorDrawerTitle>Countries</ProfileEditorDrawerTitle>;
}
